
import React, { useState, useEffect } from 'react';
import { Poll } from '@/types/poll';
import { InsightCard, transformDatabaseCard } from '@/types/insight';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import InfoBanner from './link-poll-dialog/InfoBanner';
import CurrentLinkNotice from './link-poll-dialog/CurrentLinkNotice';
import CardSelector from './link-poll-dialog/CardSelector';
import DialogActions from './link-poll-dialog/DialogActions';

interface LinkPollToCardDialogProps {
  poll: Poll | null;
  onLink: (cardId: string) => void;
  onCancel: () => void;
}

const LinkPollToCardDialog: React.FC<LinkPollToCardDialogProps> = ({ 
  poll, 
  onLink, 
  onCancel 
}) => {
  const [cards, setCards] = useState<InsightCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('insight_cards')
          .select('*');
          
        if (error) {
          console.error('Error fetching cards from Supabase:', error);
          throw error;
        }
        
        if (data && data.length > 0) {
          const transformedCards = data.map(dbCard => transformDatabaseCard(dbCard));
          setCards(transformedCards);
          
          if (poll?.linked_card_id) {
            setSelectedCardId(poll.linked_card_id);
          }
        } else {
          const storedCards = localStorage.getItem('insightCards');
          if (storedCards) {
            const parsedCards = JSON.parse(storedCards);
            setCards(parsedCards);
            
            if (poll?.linked_card_id) {
              setSelectedCardId(poll.linked_card_id);
            }
          } else {
            setCards([]);
          }
        }
      } catch (error) {
        console.error('Error loading cards:', error);
        
        const storedCards = localStorage.getItem('insightCards');
        if (storedCards) {
          setCards(JSON.parse(storedCards));
          
          if (poll?.linked_card_id) {
            setSelectedCardId(poll.linked_card_id);
          }
        } else {
          setCards([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCards();
  }, [poll]);

  const handleSubmit = () => {
    if (selectedCardId) {
      onLink(selectedCardId);
    }
  };

  if (!poll) return null;

  const filteredCards = cards.filter(card => 
    card.category === poll.category &&
    (card.country_code === poll.country_code || 
     (!card.country_code && !poll.country_code))
  );

  const availableCards = filteredCards.length > 0 ? filteredCards : cards;
  const isDisabled = !selectedCardId || isLoading || availableCards.length === 0;

  return (
    <Dialog open={true} onOpenChange={() => onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link Poll to Insight Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <InfoBanner />
          
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Link this poll to an insight card to automatically update the card's statistics based on poll results.
          </p>
          
          {poll.linked_card_id && <CurrentLinkNotice poll={poll} />}
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Select Insight Card</label>
            <CardSelector 
              availableCards={availableCards}
              selectedCardId={selectedCardId}
              setSelectedCardId={setSelectedCardId}
              isLoading={isLoading}
              poll={poll}
            />
          </div>
          
          <DialogActions 
            onCancel={onCancel}
            onSubmit={handleSubmit}
            isDisabled={isDisabled}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkPollToCardDialog;
